import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ManifestRestaurantInfo,
  ThemeConfig,
  ThemeManifest
} from '../interfaces/theme-config.interface';
import { text2Kebab } from '../../shared/utils/text.utils';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CustomThemeService {
  protected readonly DEFAULT_THEME: ThemeConfig = {
    colors: {
      primary: '#80ced7',
      secondary: '#2e2b4b',
      amountSelectorBg: '#2d3c48',
      amountSelectorText: '#ffffff',
      iconColor: '#292556'
    },
    buttonStyles: {
      primary: {
        color: '#ffffff',
        bg: '#80ced7',
        hover: '#6ab3bc'
      },
      secondary: {
        color: '#ffffff',
        bg: '#000000',
        hover: '#dadada'
      },
      contrast: {
        color: '#ffffff',
        bg: '#000000',
        hover: '#333333'
      },
      transparent: {
        color: '#000000',
        bg: 'transparent',
        hover: '#f5f5f5'
      },
      danger: {
        color: '#ffffff',
        bg: '#dc2626',
        hover: '#b91c1c'
      }
    },
    buttonSizes: {
      small: {
        borderRadius: '4px'
      },
      medium: {
        borderRadius: '8px'
      },
      big: {
        borderRadius: '10px'
      },
      large: {
        borderRadius: '10px'
      },
      icon: {
        borderRadius: '50%'
      }
    },
    fontFamily: 'Satoshi'
  };

  private defaultThemeConfig: ThemeConfig;
  private currentThemeSubject = new BehaviorSubject<ThemeConfig | null>(null);
  private restaurantBrandingSubject = new BehaviorSubject<ManifestRestaurantInfo | null>(null);
  private currentManifestSubject = new BehaviorSubject<ThemeManifest | null>(null);

  public currentTheme$: Observable<ThemeConfig | null> = this.currentThemeSubject.asObservable();
  public restaurantBranding$: Observable<ManifestRestaurantInfo | null> =
    this.restaurantBrandingSubject.asObservable();
  public currentManifest$: Observable<ThemeManifest | null> =
    this.currentManifestSubject.asObservable();

  getCurrentManifest(): ThemeManifest | null {
    return this.currentManifestSubject.value;
  }

  getManifestRestaurant(): ManifestRestaurantInfo | null {
    return this.restaurantBrandingSubject.value;
  }

  getManifestGoogleAnalyticsId(): string | null {
    return this.currentManifestSubject.value?.googleAnalyticsId || null;
  }

  constructor() {
    this.defaultThemeConfig = {
      colors: (environment as any).colors || this.DEFAULT_THEME.colors,
      buttonStyles: (environment as any).buttonStyles || this.DEFAULT_THEME.buttonStyles,
      buttonSizes: (environment as any).buttonSizes || this.DEFAULT_THEME.buttonSizes,
      fontFamily: (environment as any).fontFamily || this.DEFAULT_THEME.fontFamily
    };
  }

  async initializeTheme(): Promise<void> {
    try {
      this.applyTheme(this.defaultThemeConfig);
    } catch (error) {
      console.error('Failed to initialize theme:', error);
    }
  }

  /**
   * Apply a tenant manifest (from /assets/manifests/{tenant}.{store}.json) on top of
   * the baked-in environment defaults. Missing fields fall back to whatever
   * was provided by the environment file.
   */
  applyManifest(manifest: ThemeManifest | null | undefined): void {
    const base = this.defaultThemeConfig;
    const merged: ThemeConfig = {
      colors: { ...base.colors, ...(manifest?.colors || {}) },
      buttonStyles: this.mergeButtonStyles(base.buttonStyles, manifest?.buttonStyles),
      buttonSizes: this.mergeButtonSizes(base.buttonSizes, manifest?.buttonSizes),
      fontFamily: manifest?.fontFamily || base.fontFamily
    };

    this.applyTheme(merged);

    this.currentManifestSubject.next(manifest ? { ...manifest } : null);

    if (manifest?.restaurant) {
      this.restaurantBrandingSubject.next({ ...manifest.restaurant });
      this.applyBranding(manifest.restaurant);
    }
  }

  applyTheme(config: ThemeConfig): void {
    const root = document.documentElement;

    Object.entries(config.colors).forEach(([key, value]) => {
      const cssVarName = `--${text2Kebab(key)}`;
      root.style.setProperty(cssVarName, value);
    });

    Object.entries(config.buttonStyles).forEach(([styleName, styleConfig]) => {
      const prefix = `--button-${text2Kebab(styleName)}`;
      root.style.setProperty(`${prefix}-color`, styleConfig.color);
      root.style.setProperty(`${prefix}-bg`, styleConfig.bg);
      root.style.setProperty(`${prefix}-hover`, styleConfig.hover);
    });

    Object.entries(config.buttonSizes).forEach(([sizeName, sizeConfig]) => {
      const cssVarName = `--button-${text2Kebab(sizeName)}-radius`;
      root.style.setProperty(cssVarName, sizeConfig.borderRadius);
    });

    if (config.fontFamily) {
      root.style.setProperty('--font-family-primary', config.fontFamily);
    }

    this.currentThemeSubject.next(config);
  }

  resetToDefault(): void {
    if (this.defaultThemeConfig) {
      this.applyTheme(this.defaultThemeConfig);
    }
  }

  exportThemeAsJson(): string {
    const currentTheme = this.currentThemeSubject.value;
    return currentTheme ? JSON.stringify(currentTheme, null, 2) : '{}';
  }

  private mergeButtonStyles(
    base: ThemeConfig['buttonStyles'],
    override: Partial<ThemeConfig['buttonStyles']> | undefined
  ): ThemeConfig['buttonStyles'] {
    if (!override) {
      return base;
    }
    const result = { ...base };
    (Object.keys(result) as Array<keyof ThemeConfig['buttonStyles']>).forEach((key) => {
      const partial = override[key];
      if (partial) {
        result[key] = { ...result[key], ...partial };
      }
    });
    return result;
  }

  private mergeButtonSizes(
    base: ThemeConfig['buttonSizes'],
    override: Partial<ThemeConfig['buttonSizes']> | undefined
  ): ThemeConfig['buttonSizes'] {
    if (!override) {
      return base;
    }
    const result = { ...base };
    (Object.keys(result) as Array<keyof ThemeConfig['buttonSizes']>).forEach((key) => {
      const partial = override[key];
      if (partial) {
        result[key] = { ...result[key], ...partial };
      }
    });
    return result;
  }

  /**
   * Runtime updates for the per-tenant branding that isn't covered by CSS
   * variables: favicon link element, document title when a restaurant name
   * is supplied by the manifest.
   */
  private applyBranding(branding: ManifestRestaurantInfo): void {
    if (typeof document === 'undefined') {
      return;
    }

    if (branding.name) {
      document.title = branding.name;
    }

    if (branding.favicon) {
      this.setFavicon(branding.favicon);
    }
  }

  private setFavicon(href: string): void {
    const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (existing) {
      existing.href = href;
      return;
    }
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = href;
    document.head.appendChild(link);
  }
}
